import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { IconButton } from "@mui/material";
import { FaFileImport } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import actions from "store/actions";
import { State } from "store/reducers";
import { NFSeState } from "store/reducers/nfse";
import Spinner from "Components/Spinner";
import dayjs from "dayjs";

import { removeDiacritics } from "helpers/string";
import INFSe from "Types/INFSe";

const MAX_PARSE_ERRORS = 25;

type ImportSourceMeta = {
	sheetRow: number;
	valueCol?: string;
	filenameCol?: string;
	documentCol?: string;
	cnpjCol?: string;
};

function normalizeHeaderText(v: unknown): string {
	return removeDiacritics(String(v ?? "").trim().toLowerCase());
}

function cellAddr(r: number, c: number): string {
	return XLSX.utils.encode_cell({ r, c });
}

function colLetter(c: number): string {
	return XLSX.utils.encode_col(c);
}

/** Cabeçalhos esperados na planilha (após normalização). */
const HDR = {
	entregador: "entregador",
	cnpj: "cnpj",
	senha: "senha",
	cnpjEmpresa: "cnpj empresa",
	nomeEmpresa: "nome empresa",
	competencia: "data de competencia",
	valor: "valor",
	nomeArquivo: "nome do arquivo",
	descricao: "descricao",
	tipoServico: "tipo de servico",
	codigoNbs: "codigo nbs",
	email: "e-mail",
	codMunicipio: "codigo municipio",
	descMunicipio: "descricao municipio",
	codComplMunicipal: "codigo complementar municipal"
} as const;

const REQUIRED_HEADERS: readonly string[] = [
	HDR.cnpj,
	HDR.cnpjEmpresa,
	HDR.valor,
	HDR.nomeArquivo,
	HDR.entregador
];

function buildHeaderMap(sheet: XLSX.WorkSheet, headerRow: number): Map<string, number> {
	const ref = sheet["!ref"];
	if (!ref) return new Map();
	const range = XLSX.utils.decode_range(ref);
	const map = new Map<string, number>();
	for (let c = range.s.c; c <= range.e.c; c++) {
		const addr = cellAddr(headerRow, c);
		const cell = sheet[addr];
		if (!cell) continue;
		const key = normalizeHeaderText(cell.v);
		if (key) map.set(key, c);
	}
	return map;
}

function parseNFSeRowsFromSheet(
	sheet: XLSX.WorkSheet,
	ref: XLSX.Range
): { data: (Record<string, unknown> & { importSource: ImportSourceMeta })[]; errors: string[] } {
	const errors: string[] = [];
	const headerRow = ref.s.r;
	const headerToCol = buildHeaderMap(sheet, headerRow);

	for (const h of REQUIRED_HEADERS) {
		if (!headerToCol.has(h)) {
			errors.push(
				`Cabeçalho obrigatório ausente na linha ${headerRow + 1}: "${h}". Verifique a primeira linha da planilha.`
			);
		}
	}
	if (errors.length) {
		return { data: [], errors };
	}

	const getCell = (r: number, headerKey: string): unknown => {
		const col = headerToCol.get(headerKey);
		if (col === undefined) return undefined;
		const cell = sheet[cellAddr(r, col)];
		return cell?.v;
	};

	const data: (Record<string, unknown> & { importSource: ImportSourceMeta })[] = [];

	for (let r = headerRow + 1; r <= ref.e.r; r++) {
		const sheetRow = r + 1;
		const documentRaw = getCell(r, HDR.cnpj);
		const cnpjEmpresaRaw = getCell(r, HDR.cnpjEmpresa);
		const valorRaw = getCell(r, HDR.valor);

		const blankRow =
			(documentRaw == null || String(documentRaw).trim() === "") &&
			(cnpjEmpresaRaw == null || String(cnpjEmpresaRaw).trim() === "") &&
			(valorRaw == null || String(valorRaw).trim() === "");
		if (blankRow) continue;

		const importSource: ImportSourceMeta = {
			sheetRow,
			valueCol: headerToCol.has(HDR.valor) ? colLetter(headerToCol.get(HDR.valor)!) : undefined,
			filenameCol: headerToCol.has(HDR.nomeArquivo)
				? colLetter(headerToCol.get(HDR.nomeArquivo)!)
				: undefined,
			documentCol: headerToCol.has(HDR.cnpj) ? colLetter(headerToCol.get(HDR.cnpj)!) : undefined,
			cnpjCol: headerToCol.has(HDR.cnpjEmpresa)
				? colLetter(headerToCol.get(HDR.cnpjEmpresa)!)
				: undefined
		};

		const name = getCell(r, HDR.entregador);
		const document = documentRaw;
		const password = getCell(r, HDR.senha);
		const cnpj = cnpjEmpresaRaw;
		const companyName = getCell(r, HDR.nomeEmpresa);
		const competencia = getCell(r, HDR.competencia);
		const filename = getCell(r, HDR.nomeArquivo);
		const service = getCell(r, HDR.descricao);
		const serviceType = getCell(r, HDR.tipoServico);
		const codigoNbs = getCell(r, HDR.codigoNbs);
		const email = getCell(r, HDR.email);
		const municipalityCode = getCell(r, HDR.codMunicipio);
		const municipalityDescription = getCell(r, HDR.descMunicipio);
		const complementaryMunicipalCode = getCell(r, HDR.codComplMunicipal);

		const trimmedValor =
			valorRaw === undefined || valorRaw === null ? "" : String(valorRaw).trim();
		const nValor = trimmedValor === "" ? Number.NaN : Number(trimmedValor);
		const value = Number.isFinite(nValor) ? Math.round(nValor * 100) : Number.NaN;

		const rowErrors: string[] = [];

		if (!String(name ?? "").trim()) {
			const cc = headerToCol.has(HDR.entregador) ? colLetter(headerToCol.get(HDR.entregador)!) : "?";
			rowErrors.push(
				`Linha ${sheetRow}, coluna ${cc} (entregador): nome do prestador é obrigatório.`
			);
		}

		const docDigits = String(document ?? "")
			.match(/\d/g)
			?.join("");
		if (!docDigits) {
			rowErrors.push(
				`Linha ${sheetRow}, coluna ${importSource.documentCol ?? "?"} (cnpj): CNPJ do prestador é obrigatório.`
			);
		}

		const cnpjDigits = String(cnpj ?? "")
			.match(/\d/g)
			?.join("");
		if (!cnpjDigits) {
			rowErrors.push(
				`Linha ${sheetRow}, coluna ${importSource.cnpjCol ?? "?"} (cnpj empresa): CNPJ da empresa é obrigatório.`
			);
		}

		if (!String(filename ?? "").trim()) {
			rowErrors.push(
				`Linha ${sheetRow}, coluna ${importSource.filenameCol ?? "?"} (nome do arquivo): campo obrigatório.`
			);
		}

		if (!Number.isFinite(value)) {
			rowErrors.push(
				`Linha ${sheetRow}, coluna ${importSource.valueCol ?? "?"} (valor): informe um número válido (ex.: 100 ou 99,90). Célula vazia ou texto não numérico não é aceita.`
			);
		}

		if (rowErrors.length) {
			for (const e of rowErrors) {
				if (errors.length >= MAX_PARSE_ERRORS) break;
				errors.push(e);
			}
			if (errors.length >= MAX_PARSE_ERRORS) {
				errors.push("… (demais erros omitidos; corrija as linhas acima e importe novamente.)");
				return { data: [], errors };
			}
			continue;
		}

		const competenceStr = competencia
			? dayjs(competencia as string | number | Date, "DD/MM/YYYY").format("YYYY-MM-DD HH:mm:ss Z")
			: "";

		data.push({
			name,
			document,
			password,
			cnpj,
			companyName,
			competenceDate: competenceStr,
			value,
			filename,
			service,
			serviceType,
			nbsCode: codigoNbs,
			email,
			municipalityCode,
			municipalityDescription,
			complementaryMunicipalCode,
			importSource
		});
	}

	return { data, errors };
}

function ImportNFSe({ load }: any) {
	const dispatch = useDispatch<any>();

	const [exportComponent, setExport] = useState<React.ReactNode>(null);

	const { loading } = useSelector<State, NFSeState>(s => s.nfse);

	const inputFile: any = useRef<HTMLInputElement>(null);

	const openSelectFile = () => {
		if (inputFile.current) {
			inputFile.current.click();
		}
	};

	const handleExport = (_data: INFSe[]) => {
		setTimeout(() => {
			setExport(null);
		}, 1000);
	};

	const handleFileSelected = (e: any) => {
		const file = e.target.files[0];

		if (!file) return;

		e.target.value = null;

		const reader = new FileReader();
		const rABS = !!reader.readAsBinaryString;
		reader.onload = (ev: ProgressEvent<FileReader>) => {
			const bstr = ev.target?.result;
			const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
			const wsname = wb.SheetNames[0];
			const sheet = wb.Sheets[wsname];
			const ref = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : null;

			if (!ref) {
				alert("Planilha vazia ou intervalo inválido.");
				return;
			}

			const headerRow = ref.s.r;
			for (let c = ref.s.c; c <= ref.e.c; ++c) {
				const addr = cellAddr(headerRow, c);
				const cell = sheet[addr];
				if (cell && cell.t === "s") {
					cell.v = normalizeHeaderText(cell.v);
					if (cell.w) cell.w = normalizeHeaderText(cell.w);
				} else if (cell) {
					cell.v = normalizeHeaderText(cell.v);
				}
			}

			const { data, errors } = parseNFSeRowsFromSheet(sheet, ref);

			if (errors.length) {
				alert(errors.join("\n\n"));
				return;
			}

			if (!data.length) {
				alert("Nenhuma linha de dados encontrada após o cabeçalho. Preencha ao menos uma linha.");
				return;
			}

			dispatch(
				actions.nfse.importNFSes(data, (err, data: any) => {
					if (err) {
						let msg: string;
						if (typeof err === "string") {
							msg = err;
						} else if (err && typeof err === "object" && "default" in err) {
							const d = (err as { default: unknown }).default;
							msg = typeof d === "string" ? d : JSON.stringify(d);
						} else {
							msg = JSON.stringify(err);
						}
						alert(msg);
					} else {
						load();
						handleExport(data);
					}
				})
			);
		};

		if (rABS) reader.readAsBinaryString(file);
		else reader.readAsArrayBuffer(file);

		if (inputFile.current) inputFile.current.value = "";
	};

	return (
		<div>
			{loading.import ? (
				<Spinner size={24} />
			) : (
				<IconButton onClick={openSelectFile}>
					<FaFileImport />
				</IconButton>
			)}
			<input
				ref={inputFile}
				style={{ display: "none" }}
				type="file"
				accept="application/excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				onChange={handleFileSelected}
			/>
			{exportComponent}
		</div>
	);
}

export default ImportNFSe;
